import React, { useState, useRef } from 'react';
import { Tag, seg } from '../tags/Tag';
import { CheckmarkFilled, ErrorFilled, InProgress, WarningFilled, Information } from '@core/icons-react';
import { ActionDropdown } from '../dropdown/ActionDropdown';
import type { StateCellProps, StateVariant } from './types';

/**
 * StateCell - Renders status indicators with icons and optional dropdown actions
 * 
 * Features:
 * - Simplified variants: success, warning, error, info, neutral
 * - Default status mappings for common states
 * - Optional dropdown actions via config.options
 */

// Get icon component and color based on variant
const getStateIconAndColor = (variant: StateVariant): { Icon: React.ComponentType<any>; color?: string } => {
  switch (variant) {
    case 'success':
      return { Icon: CheckmarkFilled, color: 'var(--core-support-success)' };
    case 'error':
      return { Icon: ErrorFilled, color: 'var(--core-support-error)' };
    case 'warning':
      return { Icon: WarningFilled, color: 'var(--core-support-warning)' };
    case 'info':
      return { Icon: Information, color: 'var(--core-support-info)' };
    case 'neutral':
    default:
      return { Icon: InProgress, color: 'var(--core-text-secondary)' };
  }
};

// Default state mappings for common statuses
const DEFAULT_MAPPINGS: Record<string, { label: string; variant: StateVariant }> = {
  // Success states
  'active': { label: 'Active', variant: 'success' },
  'enabled': { label: 'Enabled', variant: 'success' },
  'online': { label: 'Online', variant: 'success' },
  'connected': { label: 'Connected', variant: 'success' },
  'healthy': { label: 'Healthy', variant: 'success' },
  'complete': { label: 'Complete', variant: 'success' },
  'success': { label: 'Success', variant: 'success' },
  'approved': { label: 'Approved', variant: 'success' },
  'verified': { label: 'Verified', variant: 'success' },
  
  // Error states
  'inactive': { label: 'Inactive', variant: 'error' },
  'disabled': { label: 'Disabled', variant: 'error' },
  'offline': { label: 'Offline', variant: 'error' },
  'disconnected': { label: 'Disconnected', variant: 'error' },
  'unhealthy': { label: 'Unhealthy', variant: 'error' },
  'failed': { label: 'Failed', variant: 'error' },
  'error': { label: 'Error', variant: 'error' },
  'rejected': { label: 'Rejected', variant: 'error' },
  'blocked': { label: 'Blocked', variant: 'error' },
  
  // Warning states
  'pending': { label: 'Pending', variant: 'warning' },
  'processing': { label: 'Processing', variant: 'warning' },
  'waiting': { label: 'Waiting', variant: 'warning' },
  'scheduled': { label: 'Scheduled', variant: 'warning' },
  'paused': { label: 'Paused', variant: 'warning' },
  'warning': { label: 'Warning', variant: 'warning' },
  'review': { label: 'Review', variant: 'warning' },
  
  // Info states
  'info': { label: 'Info', variant: 'info' },
  'new': { label: 'New', variant: 'info' },
  'updated': { label: 'Updated', variant: 'info' },
  'modified': { label: 'Modified', variant: 'info' },
  
  // Neutral states
  'unknown': { label: 'Unknown', variant: 'neutral' },
  'none': { label: 'None', variant: 'neutral' },
  'default': { label: 'Default', variant: 'neutral' },
  'neutral': { label: 'Neutral', variant: 'neutral' },
  'unverified': { label: 'Unverified', variant: 'neutral' },
};

export const StateCell: React.FC<StateCellProps> = ({ value, config, row, rowIndex }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const tagRef = useRef<HTMLDivElement>(null);
  
  if (value === null || value === undefined) {
    const { Icon, color } = getStateIconAndColor('neutral');
    return (
      <Tag
        segments={[seg.icon(Icon, { color }), seg.text('N/A')]}
        visualOnly
        size="sm"
      />
    );
  }
  
  const stringValue = String(value).toLowerCase();
  let label: string;
  let variant: StateVariant;
  
  // Check custom mapping first
  if (config?.mapping && config.mapping[stringValue]) {
    const mapped = config.mapping[stringValue];
    label = mapped.label;
    variant = mapped.variant;
  }
  // Check default mappings
  else if (DEFAULT_MAPPINGS[stringValue]) {
    const mapped = DEFAULT_MAPPINGS[stringValue];
    label = mapped.label;
    variant = mapped.variant;
  }
  // Use configured default variant or neutral
  else {
    variant = config?.variant || 'neutral';
    // Capitalize first letter for display
    label = String(value).charAt(0).toUpperCase() + String(value).slice(1);
  }
  
  const { Icon, color } = getStateIconAndColor(variant);
  
  // Check if this state has dropdown options configured
  const dropdownOptions = config?.options && row ? config.options(value, row) : null;
  const hasActions = dropdownOptions && dropdownOptions.length > 0;
  const showIcon = config?.showIcon !== false;
  
  // Process dropdown options for ActionDropdown format
  const processedActionOptions = dropdownOptions?.map(option => ({
    ...option,
    onClick: () => option.onClick()
  })) || [];
  
  return (
    <div ref={tagRef} style={{ display: 'inline-flex', cursor: hasActions ? 'pointer' : 'default' }}>
      <Tag
        segments={showIcon ? [seg.icon(Icon, { color }), seg.text(label)] : [seg.text(label)]}
        visualOnly={!hasActions}
        size="sm"
        onClick={hasActions ? () => setIsDropdownOpen(!isDropdownOpen) : undefined}
        active={isDropdownOpen}
      />
      
      {hasActions && (
        <ActionDropdown
          isOpen={isDropdownOpen}
          onClose={() => setIsDropdownOpen(false)}
          viewOptions={[]}
          actionOptions={processedActionOptions}
          anchorRef={tagRef}
          position="left"
          direction="down"
          size="medium"
          variant="default"
        />
      )}
    </div>
  );
};

